/**
 * Profile Service Implementation
 * Handles profile and bank account operations for returning users
 */

import type { AxiosInstance } from "axios";
import type { ProfileService } from "../contracts/profile.contract";
import type { RequestConfig } from "../types/common";
import type {
	ProfileBankAccountsResponse,
	ProfileResponse,
} from "../types/moneygram";

export class MoneyGramProfileService implements ProfileService {
	constructor(private readonly httpClient: AxiosInstance) {}

	/**
	 * Get user profile by wallet address
	 */
	async getProfileByWalletAddress(
		walletAddress: string,
		config?: RequestConfig,
	): Promise<ProfileResponse> {
		try {
			const response = await this.httpClient.get("/profile", {
				params: {
					providerAccountAddress: walletAddress,
				},
				signal: config?.signal,
				timeout: config?.timeout,
				...config,
			});

			return response.data as ProfileResponse;
		} catch (error) {
			// Handle profile not found (404) gracefully - user is not a returning user
			if (this.isAxiosError(error) && error.response?.status === 404) {
				throw new Error("PROFILE_NOT_FOUND");
			}

			throw this.handleApiError(error, "Failed to fetch user profile");
		}
	}

	/**
	 * Get bank accounts associated with a digital profile
	 */
	async getProfileBankAccounts(
		digitalProfileId: string,
		config?: RequestConfig,
	): Promise<ProfileBankAccountsResponse> {
		try {
			const response = await this.httpClient.get(
				`/profile/${digitalProfileId}/account`,
				{
					signal: config?.signal,
					timeout: config?.timeout,
					...config,
				},
			);

			return response.data as ProfileBankAccountsResponse;
		} catch (error) {
			// Handle no bank accounts (404) gracefully
			if (this.isAxiosError(error) && error.response?.status === 404) {
				// Return empty bank accounts array
				return { bankAccounts: [] };
			}

			throw this.handleApiError(error, "Failed to fetch bank accounts");
		}
	}

	/**
	 * Save/update bank account for a digital profile
	 */
	async saveBankAccount(
		digitalProfileId: string,
		request: import("../contracts/profile.contract").SaveBankAccountRequest,
		config?: RequestConfig,
	): Promise<import("../contracts/profile.contract").SaveBankAccountResponse> {
		try {
			const response = await this.httpClient.put(
				`/profile/${digitalProfileId}/account`,
				request,
				{
					signal: config?.signal,
					timeout: config?.timeout,
					...config,
				},
			);

			return response.data as import("../contracts/profile.contract").SaveBankAccountResponse;
		} catch (error) {
			throw this.handleApiError(error, "Failed to save bank account");
		}
	}

	/**
	 * Check if phone number has existing digital profile
	 */
	async checkProfileByPhone(
		request: import("../types/moneygram").PhonePreCheckRequest,
		config?: RequestConfig,
	): Promise<import("../types/moneygram").PhonePreCheckResponse> {
		try {
			const response = await this.httpClient.post(
				"/profile/pre-check",
				request,
				{
					signal: config?.signal,
					timeout: config?.timeout,
					...config,
				},
			);

			return response.data as import("../types/moneygram").PhonePreCheckResponse;
		} catch (error) {
			throw this.handleApiError(error, "Failed to check profile by phone");
		}
	}

	/**
	 * Create new digital profile for user
	 */
	async createProfile(
		request: import("../types/moneygram").CreateProfileRequest,
		config?: RequestConfig,
	): Promise<import("../types/moneygram").CreateProfileResponse> {
		try {
			const response = await this.httpClient.post(
				"/profile/registration",
				request,
				{
					signal: config?.signal,
					timeout: config?.timeout,
					...config,
				},
			);

			return response.data as import("../types/moneygram").CreateProfileResponse;
		} catch (error) {
			throw this.handleApiError(error, "Failed to create profile");
		}
	}

	/**
	 * Associate a wallet address to a digital profile
	 */
	async associateWalletToProfile(
		digitalProfileId: string,
		walletId: string,
		config?: RequestConfig,
	): Promise<void> {
		try {
			await this.httpClient.put(
				`/profile/${digitalProfileId}/wallet/${walletId}`,
				{}, // Empty object as request body
				{
					signal: config?.signal,
					timeout: config?.timeout,
					...config,
				},
			);
		} catch (error) {
			throw this.handleApiError(error, "Failed to associate wallet to profile");
		}
	}

	/**
	 * Type guard for Axios errors
	 */
	private isAxiosError(error: unknown): error is {
		response?: { status?: number; data?: unknown };
		message: string;
	} {
		return (
			typeof error === "object" &&
			error !== null &&
			"response" in error &&
			"message" in error
		);
	}

	/**
	 * Handle and transform API errors
	 */
	private handleApiError(error: unknown, defaultMessage: string): Error {
		if (error instanceof Error) {
			return new Error(`${defaultMessage}: ${error.message}`);
		}

		// Handle axios errors specifically
		if (this.isAxiosError(error)) {
			const status = error.response?.status;
			const message =
				(error.response?.data as { message?: string })?.message ||
				defaultMessage;
			return new Error(`${message} (Status: ${status})`);
		}

		// Handle network errors
		if (
			typeof error === "object" &&
			error &&
			"code" in error &&
			typeof (error as { code: string }).code === "string"
		) {
			const networkError = error as { code: string };
			return new Error(
				`${defaultMessage}: Network error (${networkError.code})`,
			);
		}

		return new Error(defaultMessage);
	}
}
